<template>
  <div>
    <div>
      <h3 class="mb-2">{{ $t('ui.content_move.configuration_modal.from_this_old_group') }}</h3>
      <SList class="mb-4 pb-2">
        <SListItem
          :id="isChannelOfCurrentSchoolYear ? 'content-move-grade-selection' : undefined"
          :title="fromOldGroupListItemCaptions.title"
          :description="
            $t('ui.content_move.configuration_modal.move_following_content.members_count', { count: fromOldGroupListItemCaptions.count })
          "
          icon="icon-group"
          icon-color="success"
          data-cy="from-old-group-channel"
          :disabled="fromOldGroupListItemCaptions.isLoading || !isChannelOfCurrentSchoolYear"
          :right-action-icon="fromOldGroupListItemCaptions.isLoading ? 'spinner-input' : undefined"
          :right-action-label-highlighted="fromOldGroupListItemCaptions.isLoading ? undefined : true"
          :right-action-label="!fromOldGroupListItemCaptions.isLoading && isChannelOfCurrentSchoolYear ? $t('global.change') : undefined"
          @click="showContentMoveGradeSelection"
        />
      </SList>

      <h3 class="mb-2">{{ $t('ui.content_move.configuration_modal.into_this_new_group') }}</h3>
      <SList class="mb-4 pb-2">
        <SListItem
          :id="!isChannelOfCurrentSchoolYear ? 'content-move-grade-selection' : undefined"
          :title="intoNewGroupListItemCaptions.title"
          :description="
            $t('ui.content_move.configuration_modal.move_following_content.members_count', { count: intoNewGroupListItemCaptions.count })
          "
          icon="icon-group"
          icon-color="success"
          data-cy="into-new-group-channel"
          :disabled="intoNewGroupListItemCaptions.isLoading || isChannelOfCurrentSchoolYear"
          :right-action-icon="intoNewGroupListItemCaptions.isLoading ? 'spinner-input' : undefined"
          :right-action-label-highlighted="intoNewGroupListItemCaptions.isLoading ? undefined : true"
          :right-action-label="!intoNewGroupListItemCaptions.isLoading && !isChannelOfCurrentSchoolYear ? $t('global.change') : undefined"
          @click="showContentMoveGradeSelection"
        />
      </SList>

      <h3 class="mb-2">{{ $t('ui.content_move.configuration_modal.move_following_content.title') }}</h3>
      <sdui-loader v-if="$wait.waiting('grade-channel-selected')" class="py-6" />
      <SList v-else class="mb-4 pb-2">
        <SListItem
          :title="$t('global.cloud')"
          data-cy="cloud-metric"
          :description="
            $t('ui.content_move.configuration_modal.move_following_content.files_count', { count: localChannelMetrics.files_count })
          "
          icon="icon-cloud"
          color="gray-300"
          :aria-pressed="moveCloudSelected ? 'true' : 'false'"
          @click.prevent="moveCloudSelected = !moveCloudSelected"
        >
          <template #right-action>
            <SCheckbox v-model="moveCloudSelected"
                       name="move-cloud"
                       :label-text="$t('global.cloud')"
            />
          </template>
        </SListItem>
        <SListItem
          :title="$t('ui.content_move.configuration_modal.chat')"
          :description="
            $t('ui.content_move.configuration_modal.move_following_content.messages_count', { count: localChannelMetrics.messages_count })
          "
          icon="icon-chat"
          data-cy="chat-metric"
          color="gray-300"
          :aria-pressed="moveChatSelected ? 'true' : 'false'"
          @click.prevent="moveChatSelected = !moveChatSelected"
        >
          <template #right-action>
            <SCheckbox v-model="moveChatSelected"
                       name="move-chat"
                       :label-text="$t('global.cloud')"
            />
          </template>
        </SListItem>
      </SList>

      <h3 class="mb-2">{{ $t('ui.content_move.configuration_modal.important_hints.title') }}</h3>
      <b-row no-gutters class="gray-300 mb-2">
        <b-col cols="auto">
          <sdui-icon class="icon-info icon-16" />
        </b-col>
        <b-col cols="11" class="ml-2">
          <i18n-t
            keypath="ui.content_move.configuration_modal.important_hints.news_hint"
            tag="h5"
            class="mb-0 font-weight-normal"
            data-cy="news-metric"
          >
            <template #newsCount>
              <span>{{ localChannelMetrics.news_count }}</span>
            </template>
            <template #newsHintBold>
              <span class="font-weight-bold black">{{ $t('ui.content_move.configuration_modal.important_hints.news_hint_bold') }}</span>
            </template>
          </i18n-t>
        </b-col>
      </b-row>
      <b-row no-gutters class="gray-300 mb-2">
        <b-col cols="auto">
          <sdui-icon class="icon-info icon-16" />
        </b-col>
        <b-col cols="11" class="ml-2">
          <i18n-t keypath="ui.content_move.configuration_modal.important_hints.channel_hint_1"
                  tag="h5"
                  class="mb-0 font-weight-normal"
          >
            <template #channelHint1Bold>
              <span class="font-weight-bold black">{{
                $t('ui.content_move.configuration_modal.important_hints.channel_hint_1_bold')
              }}</span>
            </template>
          </i18n-t>
        </b-col>
      </b-row>
      <b-row no-gutters class="gray-300">
        <b-col cols="auto">
          <sdui-icon class="icon-info icon-16" />
        </b-col>
        <b-col cols="11" class="ml-2">
          <i18n-t keypath="ui.content_move.configuration_modal.important_hints.channel_hint_2"
                  tag="h5"
                  class="mb-0 font-weight-normal"
          >
            <template #channelHint2Bold>
              <span class="font-weight-bold black">{{
                $t('ui.content_move.configuration_modal.important_hints.channel_hint_2_bold')
              }}</span>
            </template>
          </i18n-t>
        </b-col>
      </b-row>
    </div>

    <SModalPortalTo>
      <b-button block
                size="lg"
                data-cy="confirm-configuration"
                variant="primary"
                @click="showContentMoveConfirmModal = true"
      >
        {{ $t('ui.content_move.configuration_modal.confirm_content_move') }}
      </b-button>
    </SModalPortalTo>

    <SModal
      v-model="showContentMoveGradeSelectionModal"
      :header-title="gradeSelectionModalTitle"
      :show-footer-gradient="false"
      return-focus="#content-move-grade-selection"
      body-class="pt-3"
      data-cy="content-move-grade-channel-selection-from-configuration"
    >
      <sdui-content-move-grade-selection
        v-if="showContentMoveGradeSelectionModal"
        :grade-group-channels="gradeGroupChannelsForContentMove"
        :foreign-grade-group-channels="foreignGradeGroupChannelsForContentMove"
        :display-old-groups="isChannelOfCurrentSchoolYear"
        :is-change-of-suggested-group="true"
        @grade-channel-selected="handleGradeChannelSelected"
      />
    </SModal>

    <SConfirmModal
      v-model="showContentMoveConfirmModal"
      data-cy="content-move-confirm"
      :title="$t('ui.content_move.configuration_modal.last_check_before_submit_modal.title')"
      scrollable
      @hide="showContentMoveConfirmModal = false"
    >
      <template v-if="showContentMoveConfirmModal">
        <p
          data-cy="confirm-description-1"
          v-html="
            $sanitize(
              $t('ui.content_move.configuration_modal.last_check_before_submit_modal.description_1', {
                oldGroupChannelName: fromOldGroupListItemCaptions.title,
                newGroupChannelName: intoNewGroupListItemCaptions.title,
              }),
            )
          "
        />
        <ul data-cy="confirm-metric" class="pl-4">
          <li>
            {{
              $t('ui.content_move.configuration_modal.last_check_before_submit_modal.description_2', {
                count: moveCloudSelected ? localChannelMetrics.files_count : 0,
              })
            }}
          </li>
          <li>
            {{
              $t('ui.content_move.configuration_modal.last_check_before_submit_modal.description_3', {
                count: moveChatSelected ? localChannelMetrics.messages_count : 0,
              })
            }}
          </li>
        </ul>
        <p>{{ $t('ui.content_move.configuration_modal.last_check_before_submit_modal.description_4') }}</p>
        <p
          data-cy="confirm-description-2"
          class="mb-0"
          v-html="
            $sanitize(
              $t('ui.content_move.configuration_modal.last_check_before_submit_modal.description_5', {
                oldGroupChannelName: fromOldGroupListItemCaptions.title,
              }),
            )
          "
        />
      </template>
      <template #modal-footer>
        <b-button block
                  size="lg"
                  variant="link"
                  data-cy="content-move-cancel"
                  @click="showContentMoveConfirmModal = false"
        >
          {{ $t('ui.content_move.configuration_modal.last_check_before_submit_modal.cancel') }}
        </b-button>
        <!-- eslint-disable-next-line sdui/require-focus-restore-on-disabled-button -->
        <b-button
          block
          size="lg"
          variant="primary"
          data-cy="content-move-submit"
          :class="{ 'spinner-btn': $wait.waiting('submit-content-move') }"
          :disabled="$wait.waiting('submit-content-move')"
          @click="submitContentMove"
        >
          {{ $t('ui.content_move.configuration_modal.last_check_before_submit_modal.submit') }}
        </b-button>
      </template>
    </SConfirmModal>
  </div>
</template>

<script>
import SModalPortalTo from 'src/components/base/SModal/SModalPortalTo.vue'
import SduiContentMoveGradeSelection from 'src/components/contentMove/SduiContentMoveGradeSelection.vue'
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import { Channel } from 'src/store/models/index.js'
import Channels from 'src/api/channels.js'
import SCheckbox from 'src/components/base/SCheckbox.vue'

export default {
  name: 'SduiContentMoveConfiguration',

  components: {
    SModalPortalTo,
    SduiContentMoveGradeSelection,
    SduiLoader,
    SCheckbox,
  },

  props: {
    channel: {
      type: Object,
      required: true,
    },

    channelMetrics: {
      type: Object,
      required: true,
    },

    isChannelOfCurrentSchoolYear: {
      type: Boolean,
      required: true,
    },

    suggestedGroupChannel: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    moveCloudSelected: true,
    moveChatSelected: true,
    gradeGroupChannelsForContentMove: [],
    foreignGradeGroupChannelsForContentMove: {},
    showContentMoveGradeSelectionModal: false,
    showContentMoveConfirmModal: false,
    localSuggestedGroupChannel: {},
    localChannelMetrics: {},
  }),

  computed: {
    fromOldGroupListItemCaptions() {
      const title = this.isChannelOfCurrentSchoolYear ? this.localSuggestedGroupChannel.meta.displayname : this.channel.meta.displayname
      const usersCount = this.isChannelOfCurrentSchoolYear ? this.localSuggestedGroupChannel.users_count : this.channel.users_count
      return {
        title: title,
        count: usersCount,
        isLoading: this.isChannelOfCurrentSchoolYear && this.$wait.waiting('available-grades-content-move'),
      }
    },

    intoNewGroupListItemCaptions() {
      const title = this.isChannelOfCurrentSchoolYear ? this.channel.meta.displayname : this.localSuggestedGroupChannel.meta.displayname
      const usersCount = this.isChannelOfCurrentSchoolYear ? this.channel.users_count : this.localSuggestedGroupChannel.users_count
      return {
        title: title,
        count: usersCount,
        isLoading: !this.isChannelOfCurrentSchoolYear && this.$wait.waiting('available-grades-content-move'),
      }
    },

    gradeSelectionModalTitle() {
      if (this.isChannelOfCurrentSchoolYear) return this.$t('ui.content_move.grade_selection_modal.title_old_groups')
      return this.$t('ui.content_move.grade_selection_modal.title_new_groups')
    },
  },

  emits: ['close'],

  methods: {
    showContentMoveGradeSelection: waitFor('available-grades-content-move', async function () {
      const res = await Channels.getRelatedChannelsForContentMove(this.channel.id)

      this.gradeGroupChannelsForContentMove = [...res.data.self]
      this.foreignGradeGroupChannelsForContentMove = res.data.others

      this.showContentMoveGradeSelectionModal = true
    }),

    submitContentMove: waitFor('submit-content-move', async function () {
      const oldChannelId = this.isChannelOfCurrentSchoolYear ? this.localSuggestedGroupChannel.id : this.channel.id
      const newChannelId = this.isChannelOfCurrentSchoolYear ? this.channel.id : this.localSuggestedGroupChannel.id

      const res = await Channels.postChannelContentMoveSubmit({
        old_channel_id: oldChannelId,
        new_channel_id: newChannelId,
        cloud: this.moveCloudSelected,
        chat: this.moveChatSelected,
      })

      const channel = this.isChannelOfCurrentSchoolYear ? res.data.new_channel : res.data.old_channel
      if (!channel) return

      await Channel.insertOrUpdate({ data: channel })

      this.showContentMoveConfirmModal = false
      this.$emit('close')

      // Redirect to channel overview as the old channel gets immediately deactivated
      if (!this.isChannelOfCurrentSchoolYear) return this.$router.replace({ name: 'channels' })
    }),

    handleGradeChannelSelected: waitFor('grade-channel-selected', async function (channel) {
      this.showContentMoveGradeSelectionModal = false
      // if the source grade-channel was changed we need to fetch the channel metrics again
      this.localSuggestedGroupChannel = this.$clone(channel)

      // metrics are only fetched again if the old group-channel was changed which is only possible if the current
      // channel is not from current school year
      if (!this.isChannelOfCurrentSchoolYear) return
      const res = await Channels.getChannelsByIdMetrics(this.localSuggestedGroupChannel.id)
      this.localChannelMetrics = res.data
    }),
  },

  watch: {
    suggestedGroupChannel: {
      handler: function (newVal) {
        this.localSuggestedGroupChannel = this.$clone(newVal)
      },

      immediate: true,
    },

    channelMetrics: {
      handler: function (newVal) {
        this.localChannelMetrics = this.$clone(newVal)
      },

      immediate: true,
    },
  },
}
</script>
